import { useEffect, useState } from 'react';
import { Maximize2 } from 'lucide-react';
import { apiClient } from '../api/client';
import { ORDER_STATUS_LABELS, type Order, type OrderStatus } from '../types';
import { formatMoney } from '../utils/money';
import { useBoardUpdates } from '../hooks/useBoardUpdates';
import { useTechnicians } from '../hooks/useTechnicians';
import { filterOrders } from '../utils/orderFilters';
import { groupOrdersByDate } from '../utils/dateGroups';
import OrdersFilterBar from './OrdersFilterBar';
import LoadingIndicator from './LoadingIndicator';

const ACTIVE_STATUS_FILTERS: { label: string; value: string }[] = [
  { label: 'Все активные', value: 'active' },
  { label: 'Новые', value: 'NEW' },
  { label: 'Назначены', value: 'ASSIGNED' },
  { label: 'В пути', value: 'EN_ROUTE' },
  { label: 'На месте', value: 'ARRIVED' },
  { label: 'В работе', value: 'IN_PROGRESS' },
  { label: 'Отказ клиента', value: 'CLIENT_DECLINED' },
  { label: 'Остановлен', value: 'PAUSED' },
  { label: 'Завершены', value: 'COMPLETED' },
  { label: 'Отменены', value: 'CANCELLED' },
];

const TERMINAL_ORDER_STATUSES: OrderStatus[] = ['COMPLETED', 'CANCELLED'];

function countFor(counts: Record<OrderStatus, number> | null, value: string): number | null {
  if (!counts) return null;
  if (value === 'active') {
    return Object.entries(counts).reduce(
      (sum, [status, n]) => sum + (TERMINAL_ORDER_STATUSES.includes(status as OrderStatus) ? 0 : n),
      0,
    );
  }
  return counts[value as OrderStatus] ?? 0;
}

function toLocalInputValue(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

// "+1 ч 12 мин" под временем просроченного наряда — как в макете
function overdueDelta(scheduledAt: string): string {
  const diffMin = Math.round((Date.now() - new Date(scheduledAt).getTime()) / 60000);
  const hours = Math.floor(diffMin / 60);
  const minutes = diffMin % 60;
  return hours > 0 ? `+${hours} ч ${minutes} мин` : `+${minutes} мин`;
}

function yandexMapsUrl(address: string): string {
  return `https://yandex.ru/maps/?text=${encodeURIComponent(address)}`;
}

// "Тип" в таблице — реального единого поля под это в системе нет (в отличие от макета),
// собираем из того, что реально есть: юрлицо по договору — самое похожее на "Договор" из
// макета, иначе — requestType (свободный текст из Битрикса типа "Внутренний засор")
function orderTypeLabel(order: Order): string | null {
  if (order.isLegalEntity) return 'Договор';
  return order.requestType || null;
}

const TERMINAL_STATUSES = ['COMPLETED', 'CANCELLED'];

interface OrdersPanelProps {
  onOrderClick?: (orderId: string) => void;
}

export default function OrdersPanel({ onOrderClick }: OrdersPanelProps) {
  const [orders, setOrders] = useState<Order[]>([]);
  const technicians = useTechnicians();
  const [filter, setFilter] = useState('active');
  const [technicianId, setTechnicianId] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  // Время наряда в таблице — компактный текст, пока не кликнули; как в макете, где ячейка
  // времени превращается в рамку-инпут только у той строки, что сейчас редактируют
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(null);
  // Инпуты в строках — неконтролируемые (defaultValue), значение не подхватывается само
  // при перерендере. Если сохранение упало, бампаем счётчик конкретного наряда, чтобы
  // форсировать remount и вернуть на экране реальное значение с сервера
  const [fieldResetKeys, setFieldResetKeys] = useState<Record<string, number>>({});

  function bumpFieldReset(orderId: string) {
    setFieldResetKeys((prev) => ({ ...prev, [orderId]: (prev[orderId] ?? 0) + 1 }));
  }

  async function loadOrders() {
    setLoading(true);
    try {
      // 'active' — специальное значение бэкенда, означает "все, кроме завершённых/отменённых"
      // (см. OrdersService.findAll) — раньше тут всегда тянулась вся история нарядов, а
      // терминальные статусы отфильтровывались уже в браузере
      const { data } = await apiClient.get<Order[]>('/orders', { params: { status: filter } });
      setOrders(data);
      setLoadError(null);
    } catch (err) {
      console.error('Не удалось загрузить наряды', err);
      setLoadError('Не удалось загрузить наряды — попробуйте обновить страницу');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadOrders();
    // Живое обновление уже приходит по сокету (см. useBoardUpdates ниже) — этот интервал
    // только подстраховка на случай обрыва сокета
    const interval = setInterval(loadOrders, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useBoardUpdates(['orders', 'acts'], loadOrders);

  // Счётчики на чипах фильтра — отдельный лёгкий запрос (не зависит от того, какой фильтр
  // выбран сейчас), чтобы диспетчер видел, сколько нарядов в КАЖДОМ статусе, а не только в текущем
  const [statusCounts, setStatusCounts] = useState<Record<OrderStatus, number> | null>(null);
  function loadStatusCounts() {
    apiClient
      .get<Record<OrderStatus, number>>('/orders/stats/by-status')
      .then(({ data }) => setStatusCounts(data))
      .catch((err) => console.error('Не удалось загрузить счётчики статусов', err));
  }
  useEffect(() => {
    loadStatusCounts();
    const interval = setInterval(loadStatusCounts, 30000);
    return () => clearInterval(interval);
  }, []);
  useBoardUpdates(['orders'], loadStatusCounts);

  async function assignTechnician(orderId: string, technicianId: string) {
    if (!technicianId) return;
    try {
      await apiClient.patch(`/orders/${orderId}/assign`, { technicianId });
      setActionError(null);
      loadOrders();
    } catch (err) {
      console.error('Не удалось назначить сантехника', err);
      setActionError('Не удалось сохранить — попробуйте ещё раз');
      bumpFieldReset(orderId);
    }
  }

  async function cancelOrder(orderId: string) {
    if (cancellingId || !confirm('Отменить наряд?')) return;
    // Пока запрос идёт, кнопка неактивна — иначе двойной клик слал две отмены подряд (WEB-8)
    setCancellingId(orderId);
    try {
      await apiClient.patch(`/orders/${orderId}/cancel`);
      setActionError(null);
      loadOrders();
    } catch (err) {
      console.error('Не удалось отменить наряд', err);
      setActionError('Не удалось отменить наряд — попробуйте ещё раз');
    } finally {
      setCancellingId(null);
    }
  }

  async function updateSchedule(orderId: string, value: string) {
    setEditingScheduleId(null);
    if (!value) return;
    try {
      await apiClient.patch(`/orders/${orderId}/schedule`, {
        scheduledAt: new Date(value).toISOString(),
      });
      setActionError(null);
      loadOrders();
    } catch (err) {
      console.error('Не удалось изменить время наряда', err);
      setActionError('Не удалось сохранить время — попробуйте ещё раз');
      bumpFieldReset(orderId);
    }
  }

  const visibleOrders = filterOrders(orders, technicianId, search);
  const dateGroups = groupOrdersByDate(visibleOrders);

  function renderOrderRow(order: Order) {
    const terminal = TERMINAL_STATUSES.includes(order.status);
    const overdue = !!order.scheduledAt && !terminal && new Date(order.scheduledAt).getTime() < Date.now();
    const act = order.act;
    const actTotal = act ? Number(act.report?.totalAmount ?? 0) + Number(act.materialsCost ?? 0) : null;
    const typeLabel = orderTypeLabel(order);

    return (
      <tr key={order.id} className={overdue ? 'orders-row-overdue' : terminal ? 'orders-row-terminal' : ''}>
        <td className="orders-cell-number">{order.orderNumber ?? '—'}</td>
        <td>
          {!terminal && editingScheduleId === order.id ? (
            <input
              key={`schedule-${order.id}-${fieldResetKeys[order.id] ?? 0}`}
              className="orders-cell-time-input"
              type="datetime-local"
              autoFocus
              defaultValue={toLocalInputValue(order.scheduledAt)}
              onBlur={(e) => updateSchedule(order.id, e.target.value)}
            />
          ) : (
            <button
              className={`orders-cell-time-btn ${terminal ? 'orders-cell-time-btn-static' : ''}`}
              onClick={() => !terminal && setEditingScheduleId(order.id)}
              disabled={terminal}
            >
              {order.scheduledAt ? (
                <>
                  <div className="orders-cell-time">{formatTime(order.scheduledAt)}</div>
                  {overdue && <div className="orders-cell-time-overdue">{overdueDelta(order.scheduledAt)}</div>}
                </>
              ) : (
                <span className="muted">—</span>
              )}
            </button>
          )}
        </td>
        <td>
          <a
            className="orders-cell-address"
            href={yandexMapsUrl(order.address)}
            target="_blank"
            rel="noreferrer"
            title={order.address}
          >
            {order.address}
          </a>
        </td>
        <td>
          <div className="orders-cell-client-name">{order.clientName}</div>
          <a className="muted orders-cell-client-phone" href={`tel:${order.clientPhone}`}>
            {order.clientPhone}
          </a>
        </td>
        <td>{typeLabel && <span className="order-detail-tag">{typeLabel}</span>}</td>
        <td>
          {!terminal ? (
            <select
              key={`tech-${order.id}-${fieldResetKeys[order.id] ?? 0}`}
              className="orders-cell-tech-select"
              defaultValue={order.technicianId ?? ''}
              onChange={(e) => assignTechnician(order.id, e.target.value)}
            >
              <option value="">
                {order.technician ? order.technician.fullName : 'Назначить...'}
              </option>
              {technicians.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.fullName}
                </option>
              ))}
            </select>
          ) : (
            <span>{order.technician?.fullName ?? <span className="muted">—</span>}</span>
          )}
        </td>
        <td>
          <span className={`status-badge status-${order.status}`}>{ORDER_STATUS_LABELS[order.status]}</span>
        </td>
        <td className="orders-cell-sum">{actTotal !== null ? formatMoney(actTotal) : <span className="muted">—</span>}</td>
        <td className="orders-cell-actions">
          {!terminal && (
            <button
              className="btn-link orders-cell-cancel"
              onClick={() => cancelOrder(order.id)}
              disabled={cancellingId === order.id}
              title="Отменить наряд"
            >
              ✕
            </button>
          )}
          {onOrderClick && (
            <button className="orders-cell-open" title="Открыть карточку наряда" onClick={() => onOrderClick(order.id)}>
              <Maximize2 size={13} />
            </button>
          )}
        </td>
      </tr>
    );
  }

  return (
    <div className="orders-panel">
      <div className="status-filter-chips">
        {ACTIVE_STATUS_FILTERS.map((f) => {
          const count = countFor(statusCounts, f.value);
          const urgent = f.value === 'CLIENT_DECLINED';
          return (
            <button
              key={f.value}
              className={`status-filter-chip ${filter === f.value ? 'active' : ''} ${urgent ? 'status-filter-chip-danger' : ''}`}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
              {count !== null && <span className="status-filter-chip-count">{count}</span>}
            </button>
          );
        })}
      </div>

      <OrdersFilterBar
        technicians={technicians}
        technicianId={technicianId}
        onTechnicianChange={setTechnicianId}
        search={search}
        onSearchChange={setSearch}
      />

      {actionError && <p className="error-text">{actionError}</p>}
      {loadError && <p className="error-text">{loadError}</p>}

      {loading && orders.length === 0 && <LoadingIndicator label="Загрузка нарядов" />}
      {!loading && !loadError && visibleOrders.length === 0 && <p className="muted">Нет нарядов</p>}

      {visibleOrders.length > 0 && (
        <div className="orders-table-wrap">
          <table className="acts-table orders-table">
            <thead>
              <tr>
                <th className="orders-cell-number">Наряд</th>
                <th style={{ width: 100 }}>Время</th>
                <th style={{ width: 260 }}>Адрес</th>
                <th style={{ width: 190 }}>Клиент</th>
                <th style={{ width: 130 }}>Тип</th>
                <th style={{ width: 190 }}>Мастер</th>
                <th style={{ width: 140 }}>Статус</th>
                <th style={{ width: 110 }}>Сумма</th>
                <th style={{ width: 70 }}></th>
              </tr>
            </thead>
            {dateGroups.map((group) => (
              <tbody key={group.key}>
                <tr className="orders-date-divider-row">
                  <td colSpan={9}>
                    {group.label} <span className="muted">· {group.orders.length}</span>
                  </td>
                </tr>
                {group.orders.map(renderOrderRow)}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </div>
  );
}
